import { useCallback, useState } from 'react';
import { useAccount } from 'modules/taboola-common-frontend-modules/account-configurations';
import { useFormFieldValue, useFormDataContext } from 'modules/taboola-common-frontend-modules/formData';
import { useLandingPagesApi } from 'services/api/landingPagesApi';
import { chatSender } from '../consts/chatState';
import { INITIAL_VERSION } from '../consts/versionConstants';

export const useVersionNavigation = () => {
    const [isLoading, setIsLoading] = useState(false);
    const { accountName } = useAccount();
    const { value: landingPageId } = useFormFieldValue({ field: 'landingPageId' });
    const { value: versionNumber } = useFormFieldValue({ field: 'versionNumber' });
    const { value: totalVersions } = useFormFieldValue({ field: 'totalVersions' });

    const { patchExternalData } = useFormDataContext();
    const { value: currentChatHistory = [] } = useFormFieldValue({ field: 'chatHistory' });
    const { navigateToVersion, getVersions } = useLandingPagesApi();

    // Navigation helpers
    const canNavigatePrevious = versionNumber > INITIAL_VERSION;
    const canNavigateNext = versionNumber < totalVersions - 1;
    const isFirstVersion = versionNumber === INITIAL_VERSION;
    const isLatestVersion = versionNumber === totalVersions - 1;

    // Helper function to update totalVersions from backend
    const updateTotalVersions = useCallback(async () => {
        try {
            const versionsResponse = await getVersions({ accountName, landingPageId });
            const newTotalVersions = versionsResponse?.totalVersions || totalVersions;

            if (newTotalVersions !== totalVersions) {
                patchExternalData({
                    data: { totalVersions: newTotalVersions },
                    merger: (prev, data) => ({ ...prev, ...data }),
                });
            }

            return newTotalVersions;
        } catch (error) {
            console.error('Failed to update totalVersions:', error);
            return totalVersions;
        }
    }, [accountName, landingPageId, getVersions, totalVersions, patchExternalData]);

    // Navigation handler
    const handleNavigation = useCallback(
        async targetVersionNumber => {
            if (targetVersionNumber < INITIAL_VERSION || targetVersionNumber >= totalVersions) {
                console.warn(
                    `Invalid version number: ${targetVersionNumber}. Valid range: ${INITIAL_VERSION}-${
                        totalVersions - 1
                    }`
                );
                return;
            }

            setIsLoading(true);
            try {
                const navigationResponse = await navigateToVersion({
                    accountName,
                    landingPageId,
                    versionNumber: targetVersionNumber,
                });

                const { landingPage: updatedLandingPage, summary } = navigationResponse;

                if (updatedLandingPage) {
                    // Add navigation summary to existing chat history if provided
                    const updatedChatHistory = summary
                        ? [
                              ...currentChatHistory,
                              {
                                  sender: chatSender.ai,
                                  text: summary,
                              },
                          ]
                        : currentChatHistory;

                    // Only update version number and chat history for faster navigation
                    const newState = {
                        versionNumber: targetVersionNumber,
                        chatHistory: updatedChatHistory,
                        navigationTimestamp: Date.now(),
                    };

                    patchExternalData({
                        data: newState,
                        merger: (prev, data) => ({ ...prev, ...data }),
                    });
                }
            } catch (error) {
                console.error('Failed to navigate version:', error);
            } finally {
                setIsLoading(false);
            }
        },
        [accountName, landingPageId, navigateToVersion, totalVersions, patchExternalData, currentChatHistory]
    );

    // Specific navigation functions
    const navigateToPrevious = useCallback(() => {
        if (canNavigatePrevious) {
            handleNavigation(versionNumber - 1);
        }
    }, [canNavigatePrevious, versionNumber, handleNavigation]);

    const navigateToNext = useCallback(() => {
        if (canNavigateNext) {
            handleNavigation(versionNumber + 1);
        }
    }, [canNavigateNext, versionNumber, handleNavigation]);

    const navigateToSpecificVersion = useCallback(
        targetVersion => {
            handleNavigation(targetVersion);
        },
        [handleNavigation]
    );

    return {
        // Navigation state
        versionNumber,
        totalVersions,
        isLoading,

        // Navigation capabilities
        canNavigatePrevious,
        canNavigateNext,
        isFirstVersion,
        isLatestVersion,

        // Navigation functions
        navigateToPrevious,
        navigateToNext,
        navigateToSpecificVersion,
        updateTotalVersions,
    };
};
